import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Order } from '@/data/mockData';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Home, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useOrdersSort } from '@/hooks/useOrdersSort';
import { useOrdersPagination } from '@/hooks/useOrdersPagination';
import { OrdersTable } from '@/components/OrdersTable';
import { OrdersPagination } from '@/components/OrdersPagination';
import { ConfirmDeliveryDialog } from '@/components/ConfirmDeliveryDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi, API_BASE_URL, getBasicAuthHeader } from '@/lib/apiClient';
import { mapApiRequestsToOrders, ApiRequest } from '@/lib/mapApiRequestToOrder';

const ORDERS_PER_PAGE = 10;

interface ApiClientWithSegments {
  id: number;
  name: string;
}

const History = () => {
  const [confirmOrder, setConfirmOrder] = useState<Order | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, selectedClient, setClient } = useAuth();
  const queryClient = useQueryClient();

  // Récupérer l'utilisateur courant pour obtenir son ID
  const { data: currentUser } = useQuery({
    queryKey: ['current-user', user?.email],
    queryFn: async () => {
      if (!user?.email) {
        throw new Error('Utilisateur non connecté');
      }
      const userData = await usersApi.getUserByEmailUsersByEmailEmailGet(user.email);
      if (!userData || typeof userData.id !== 'number') {
        throw new Error("Utilisateur non trouvé pour cet email");
      }
      return userData;
    },
    enabled: !!user?.email,
  });

  // Récupérer la liste des clients de l'utilisateur connecté
  const { data: userClients } = useQuery({
    queryKey: ['user-clients', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) {
        throw new Error('Utilisateur non disponible');
      }
      const clientsResponse = await usersApi.getUserClientsUsersUserIdClientsGet(currentUser.id);
      return Array.isArray(clientsResponse) ? (clientsResponse as ApiClientWithSegments[]) : [];
    },
    enabled: !!currentUser?.id,
  });

  // Trouver le client courant à partir du selectedClient (nom)
  const currentClient = useMemo(() => {
    if (!selectedClient || !Array.isArray(userClients)) {
      return null;
    }
    return (userClients as ApiClientWithSegments[]).find(
      (client) => client.name === selectedClient
    ) || null;
  }, [selectedClient, userClients]);

  // Récupérer les demandes du client via API
  // Note: La limite max de l'API est 100, donc on charge par lots de 100
  // La réponse est un objet avec { items: ApiRequest[], limit: number, skip: number, total: number }
  const { data: apiRequests, isLoading: isLoadingRequests, error: requestsError } = useQuery({
    queryKey: ['client-requests', currentClient?.id],
    queryFn: async () => {
      if (!currentClient?.id) {
        throw new Error('Client non disponible');
      }
      
      // Charger toutes les demandes par lots de 100 (limite max de l'API)
      const allRequests: ApiRequest[] = [];
      let skip = 0;
      const limit = 100;
      let hasMore = true;

      while (hasMore) {
        const url = `${API_BASE_URL.replace(/\/$/, '')}/requests/client/${currentClient.id}?skip=${skip}&limit=${limit}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            accept: 'application/json',
            Authorization: getBasicAuthHeader(),
          },
        });
        
        if (!response.ok) {
          throw new Error(`Erreur API: ${response.status}`);
        }
        
        const data = await response.json();
        
        // La réponse est un objet avec { items: ApiRequest[], limit, skip, total }
        const responseItems = data?.items || (Array.isArray(data) ? data : []);
        const requests = Array.isArray(responseItems) ? (responseItems as ApiRequest[]) : [];
        
        if (requests.length === 0) {
          hasMore = false;
        } else {
          allRequests.push(...requests);
          // Si on a reçu moins que la limite, c'est qu'on a tout chargé
          if (requests.length < limit) {
            hasMore = false;
          } else {
            skip += limit;
          }
        }
      }
      
      return allRequests;
    },
    enabled: !!currentClient?.id,
  });

  // Mapper les demandes API vers Orders
  const orders = useMemo(() => {
    if (!apiRequests) {
      return [];
    }
    return mapApiRequestsToOrders(apiRequests);
  }, [apiRequests]);

  // Extract sorting logic to custom hook
  const {
    sortField,
    sortDirection,
    sortedOrders,
    handleSort,
  } = useOrdersSort(orders);

  // Extract pagination logic to custom hook
  const {
    currentPage,
    totalPages,
    currentOrders,
    startIndex,
    endIndex,
    goToNextPage,
    goToPreviousPage,
  } = useOrdersPagination(sortedOrders, ORDERS_PER_PAGE);

  const handleDownloadQuote = async (order: Order) => {
    try {
      const requestId = order.id;
      const pdfUrl = `${API_BASE_URL.replace(/\/$/, '')}/requests/${requestId}/quote-pdf`;
      const pdfResponse = await fetch(pdfUrl, {
        method: 'GET',
        headers: {
          accept: 'application/pdf',
          Authorization: getBasicAuthHeader(),
        },
      });

      if (!pdfResponse.ok) {
        throw new Error(`Erreur téléchargement PDF: ${pdfResponse.status}`);
      }

      // Ouvrir le PDF dans un nouvel onglet (blob URL)
      const blob = await pdfResponse.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      // Ne pas révoquer immédiatement l'URL blob, sinon le bouton "télécharger"
      // du visualiseur PDF du navigateur peut échouer avec une erreur réseau.
      // Le navigateur nettoiera de lui‑même les blobs associés à l'onglet quand
      // celui‑ci sera fermé.

      toast({
        title: "Devis téléchargé",
        description: `Le devis pour "${order.requestName}" a été ouvert dans un nouvel onglet.`,
      });
    } catch (error) {
      console.error('Erreur lors du téléchargement du devis:', error);
    toast({
        variant: 'destructive',
        title: 'Erreur',
        description: "Impossible de télécharger le devis. Veuillez réessayer plus tard.",
      });
    }
  };

  const handleConfirmDelivery = async (order: Order) => {
    try {
      const requestId = order.id;
      
      // 1. Lancer le job via appel HTTP direct
      const launchUrl = `${API_BASE_URL.replace(/\/$/, '')}/requests/${requestId}/launch-job`;
      const launchResponse = await fetch(launchUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          Authorization: getBasicAuthHeader(),
        },
    });

      if (!launchResponse.ok) {
        throw new Error(`Erreur lancement job: ${launchResponse.status}`);
      }

      // 2. Mettre à jour le statut à NEW_REQUEST
      const statusUrl = `${API_BASE_URL.replace(/\/$/, '')}/requests/${requestId}/status`;
      const statusResponse = await fetch(statusUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
          Authorization: getBasicAuthHeader(),
        },
        body: JSON.stringify({ status: 'NEW_REQUEST' }),
      });

      if (!statusResponse.ok) {
        throw new Error(`Erreur mise à jour statut: ${statusResponse.status}`);
      }

      toast({
        title: "Commande confirmée",
      description: `La commande "${order.requestName}" a été validée et est maintenant en cours de traitement.`,
      });
      setConfirmOrder(null);

      // Invalider la query pour recharger les données
      await queryClient.invalidateQueries({ queryKey: ['client-requests', currentClient?.id] });
    } catch (error) {
      console.error('Erreur lors de la confirmation de la livraison:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: "Impossible de confirmer la livraison. Veuillez réessayer plus tard.",
      });
    }
  };

  // Rediriger si aucun client sélectionné (vérifier aussi localStorage au cas où la réhydratation n'a pas encore eu lieu)
  const storedClient = typeof window !== 'undefined'
    ? window.localStorage.getItem('enrichment_app_selected_client')
    : null;

  // Nettoyer automatiquement un client stocké invalide et rediriger proprement
  useEffect(() => {
    if (!storedClient || !Array.isArray(userClients)) {
      return;
    }

    const clientExists = userClients.some(
      (client) => client.name === storedClient
    );

    if (!clientExists) {
      try {
        window.localStorage.removeItem('enrichment_app_selected_client');
      } catch {
        // Ignorer toute erreur d'accès au localStorage
      }
      setClient('');
      navigate('/client-selection');
    }
  }, [storedClient, userClients, setClient, navigate]);

  if (!selectedClient && !storedClient) {
    navigate('/client-selection');
    return null;
  }

  // Afficher un message de chargement
  if (isLoadingRequests) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-[90%] mx-auto p-6 flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Chargement de l'historique...</p>
          </div>
        </div>
      </div>
    );
  }

  // Afficher une erreur si le chargement a échoué
  if (requestsError) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-[90%] mx-auto p-6">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-foreground">Historique des commandes</h1>
            <Button 
              onClick={() => navigate('/configuration')}
              className="bg-primary hover:bg-primary-hover flex items-center gap-2"
            >
              <Home className="h-4 w-4" />
              Retour à la page d'accueil
            </Button>
          </div>
          <Card className="shadow-card bg-card">
            <CardContent className="p-6">
              <p className="text-destructive">
                Erreur lors du chargement de l'historique. Veuillez réessayer plus tard.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-[90%] mx-auto p-6">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-foreground">Historique des commandes</h1>
          <Button 
            onClick={() => navigate('/configuration')}
            className="bg-primary hover:bg-primary-hover flex items-center gap-2"
          >
            <Home className="h-4 w-4" />
            Retour à la page d'accueil
          </Button>
        </div>
        
        <Card className="shadow-card bg-card">
          <CardContent className="p-0">
            <OrdersTable
              orders={currentOrders}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              onDownloadQuote={handleDownloadQuote}
              onConfirmDelivery={(order) => setConfirmOrder(order)}
            />

            <OrdersPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={sortedOrders.length}
              itemsPerPage={ORDERS_PER_PAGE}
              onPreviousPage={goToPreviousPage}
              onNextPage={goToNextPage}
            />
          </CardContent>
        </Card>
      </div>

      <ConfirmDeliveryDialog
        order={confirmOrder}
        onOpenChange={(open) => !open && setConfirmOrder(null)}
        onConfirm={handleConfirmDelivery}
      />
    </div>
  );
};

export default History;